import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { Auth, setToken, clearToken, getToken } from '@/lib/api';

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  register: async () => {},
  demoLogin: async () => {},
  logout: () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const me = await Auth.me();
      setUser(me);
    } catch (_) {
      clearToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const res = await Auth.login(email, password);
    setToken(res.access_token);
    setUser(res.user);
    return res.user;
  };
  const register = async (name, email, password) => {
    const res = await Auth.register(name, email, password);
    setToken(res.access_token);
    setUser(res.user);
    return res.user;
  };
  const demoLogin = async () => {
    // ── Race-condition guard — strict version (Phase 16 fix v2) ───────
    // Multiple consumer pages call `if (!user) demoLogin()` on mount to
    // give anonymous visitors a frictionless preview. The earlier guard
    // (`loading || user`) didn't catch the case where AuthCallback had
    // just written the Google token into localStorage but hadn't yet
    // refreshed AuthProvider state — so on the very first dashboard
    // mount after a Google sign-in, every Google user got demo-stamped.
    //
    // New rule: if ANY token is in localStorage, refuse to overwrite —
    // period. If that token is invalid, `refresh()` will clear it and a
    // future demoLogin() call (now with no token) will succeed normally.
    if (getToken()) {
      return user;
    }
    const res = await Auth.demo();
    setToken(res.access_token);
    setUser(res.user);
    return res.user;
  };
  // Phase 16 — used by AuthCallback after Google sign-in to atomically
  // set the token AND sync AuthProvider state. Without this, AuthProvider
  // keeps `user=null, loading=false` after the redirect, which used to
  // mislead the demoLogin guard.
  const setSession = (token, userObj) => {
    setToken(token);
    setUser(userObj);
    setLoading(false);
  };
  const googleLogin = async (sessionId) => {
    const res = await Auth.googleSession(sessionId);
    setToken(res.access_token);
    setUser(res.user);
    return res; // includes is_new_user
  };
  const startGoogleLogin = async (redirect) => {
    const { url } = await Auth.googleLoginUrl(redirect);
    window.location.href = url;
  };
  const migrateDemo = async () => {
    return Auth.migrateDemo();
  };
  const patchMe = async (patch) => {
    const updated = await Auth.patchMe(patch);
    setUser(updated);
    return updated;
  };
  const logout = () => {
    clearToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, register, demoLogin, googleLogin, startGoogleLogin,
      patchMe, migrateDemo, refresh, setSession, logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
